import { useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

// S-S154-001: canonical boolean key; legacy 'repolens:theme' key kept for migration
const DARK_MODE_KEY = 'repolens:darkMode';
const LEGACY_KEY = 'repolens:theme';
const DEFAULT_THEME: Theme = 'light';

function isValidTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  // Hot #111: lazy useState initializer reads localStorage synchronously on first render
  // S-S154-001: prefer repolens:darkMode boolean key; fall back to legacy repolens:theme
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const darkModeStored = localStorage.getItem(DARK_MODE_KEY);
      if (darkModeStored !== null) {
        return darkModeStored === 'true' ? 'dark' : 'light';
      }
      // migrate from legacy key
      const legacy = localStorage.getItem(LEGACY_KEY);
      return isValidTheme(legacy) ? legacy : DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  });

  // Hot #115: useEffect placed AFTER derived consts (theme state fully declared above)
  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.setAttribute('data-theme', theme);
    try {
      // S-S154-001: write boolean key (primary) + legacy key (compat)
      localStorage.setItem(DARK_MODE_KEY, isDark ? 'true' : 'false');
      localStorage.setItem(LEGACY_KEY, theme);
    } catch {
      // localStorage may be unavailable in private browsing; degrade gracefully
    }
  }, [theme]);

  const toggleTheme = (): void => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return { theme, toggleTheme };
}
